"use strict";
(function () {
  var DISMISS_KEY = "embe:giftDismissed";
  // Guard against re-entrant gift enforcement (removing/capping invalid gifts).
  var enforcing = false;
  // Set by a genuine cart change; consumed by the next MutationObserver pass so the
  // popup auto-opens only after the drawer has actually re-rendered.
  var autoOpenRequested = false;
  // Debounce flag for the drawer MutationObserver.
  var observerScheduled = false;
  // Tracks the cart drawer open/close state so we only auto-open the popup on the
  // transition into "open" (never on page load).
  var drawerWasOpen = false;
  // True while a gift add request is in flight, so other add buttons are blocked.
  var addingGift = false;
  // Holds the cart payload from the last genuine cart change so enforceGifts can run
  // against FRESH DOM (after render) without an extra cart.js fetch when possible.
  var pendingCartData = null;

  function readDismissed() {
    try {
      var raw = localStorage.getItem(DISMISS_KEY);
      var arr = raw ? JSON.parse(raw) : [];
      return Array.isArray(arr) ? arr : [];
    } catch (e) {
      return [];
    }
  }

  function writeDismissed(arr) {
    try {
      localStorage.setItem(DISMISS_KEY, JSON.stringify(arr));
    } catch (e) {}
  }

  function addDismissed(handle) {
    var arr = readDismissed();
    if (arr.indexOf(handle) === -1) {
      arr.push(handle);
      writeDismissed(arr);
    }
  }

  function removeDismissed(handle) {
    var arr = readDismissed().filter(function (h) {
      return h !== handle;
    });
    writeDismissed(arr);
  }

  /* ---------------- Carousel ---------------- */
  function initCarousels(root) {
    (root || document).querySelectorAll("[data-gift-progress]").forEach(function (progress) {
      var track = progress.querySelector("[data-gift-progress-track]");
      var dotsWrap = progress.querySelector("[data-gift-dots]");
      if (!track) return;
      // Skip tracks already wired. Fresh server renders create a new track
      // element (no marker), so this stays correct across re-renders while
      // preventing MutationObserver-driven re-entry loops.
      if (track.dataset.giftCarouselInit === "1") return;
      track.dataset.giftCarouselInit = "1";

      var slides = track.querySelectorAll("[data-gift-slide]");
      var count = slides.length;

      progress.style.display = count === 0 ? "none" : "";
      progress.classList.toggle("is-single", count <= 1);
      if (count === 0) return;
      if (dotsWrap) dotsWrap.innerHTML = "";

      var index = 0;
      function go(i) {
        index = (i + count) % count;
        track.style.transform = "translateX(" + -index * 100 + "%)";
        if (dotsWrap) {
          Array.prototype.forEach.call(dotsWrap.children, function (dot, di) {
            dot.classList.toggle("is-active", di === index);
          });
        }
      }

      if (dotsWrap && count > 1) {
        for (var d = 0; d < count; d++) {
          (function (di) {
            var dot = document.createElement("button");
            dot.type = "button";
            dot.className = "gift-progress__dot" + (di === 0 ? " is-active" : "");
            dot.setAttribute("aria-label", "Offer " + (di + 1));
            dot.addEventListener("click", function () {
              go(di);
            });
            dotsWrap.appendChild(dot);
          })(d);
        }
      }

      progress.querySelectorAll("[data-gift-prev]").forEach(function (btn) {
        btn.addEventListener("click", function () {
          go(index - 1);
        });
      });
      progress.querySelectorAll("[data-gift-next]").forEach(function (btn) {
        btn.addEventListener("click", function () {
          go(index + 1);
        });
      });

      go(0);
    });
  }

  /* ---------------- Popup ---------------- */
  // The cart drawer uses transform on .drawer__inner, which would trap a
  // position:fixed popup. Move the freshly rendered popup to <body>.
  // Idempotent: safe to call repeatedly (MutationObserver may fire several times).
  function relocatePopup() {
    var giftRoot = document.querySelector("#cart-progress [data-gift-campaigns]");
    var freshPopup = giftRoot ? giftRoot.querySelector("[data-gift-popup]") : null;
    var old = document.querySelector("body > [data-gift-popup][data-relocated]");

    if (freshPopup) {
      // A new popup was just server-rendered inside the drawer: swap it to <body>.
      var wasOpen = old && old.hidden === false;
      if (old) old.parentNode.removeChild(old);
      freshPopup.setAttribute("data-relocated", "");
      if (wasOpen) freshPopup.hidden = false;
      document.body.appendChild(freshPopup);
      return;
    }

    if (!giftRoot) {
      // Gift bar no longer rendered (promotional bar shown) -> drop relocated popup.
      if (old) {
        old.parentNode.removeChild(old);
        document.body.classList.remove("gift-popup-open");
      }
    }
    // else: gift bar present but popup already relocated -> keep the existing one.
  }

  function getPopup() {
    return document.querySelector("[data-gift-popup]");
  }

  function openPopup() {
    var popup = getPopup();
    if (!popup) return;
    popup.hidden = false;
    document.body.classList.add("gift-popup-open");
  }

  function closePopup() {
    var popup = getPopup();
    if (!popup) return;
    popup.hidden = true;
    document.body.classList.remove("gift-popup-open");
  }

  // One entry per eligible campaign currently rendered in the popup, with its
  // gift allowance (max) and how many gifts are already in the cart (current).
  // canAdd = there is still room to pick another gift for that campaign.
  function getSectionStats() {
    var stats = [];
    document.querySelectorAll("[data-gift-section]").forEach(function (sec) {
      var h = sec.getAttribute("data-campaign-handle");
      if (!h) return;
      var max = parseInt(sec.getAttribute("data-max"), 10);
      var current = parseInt(sec.getAttribute("data-current"), 10);
      if (isNaN(max)) max = Infinity;
      if (isNaN(current)) current = 0;
      stats.push({ handle: h, max: max, current: current, canAdd: current < max });
    });
    return stats;
  }

  // Reflect dismissed state on checkboxes + decide auto-open/close.
  // opts.openIfEligible: open only when there is an eligible, non-dismissed campaign
  //   that STILL has room to add a gift (used on drawer open / cart change).
  // opts.keepOpen: keep the popup open after adding a gift, but close it as soon as
  //   every eligible campaign has reached its limit (nothing left to pick).
  function syncPopupState(opts) {
    relocatePopup();
    var popup = getPopup();
    var dismissed = readDismissed();

    document.querySelectorAll("[data-gift-dismiss]").forEach(function (cb) {
      var h = cb.getAttribute("data-campaign-handle");
      cb.checked = dismissed.indexOf(h) !== -1;
    });

    if (!popup) return;

    var stats = getSectionStats();
    var addable = stats.filter(function (s) {
      return s.canAdd;
    });
    var openableNotDismissed = addable.filter(function (s) {
      return dismissed.indexOf(s.handle) === -1;
    });

    var shouldOpen = false;
    if (opts && opts.keepOpen && addable.length > 0) shouldOpen = true;
    if (opts && opts.openIfEligible && openableNotDismissed.length > 0) shouldOpen = true;

    if (shouldOpen) openPopup();

    // Close when there are no eligible campaigns at all, or (right after adding a
    // gift) when every campaign has just hit its limit.
    if (stats.length === 0) {
      closePopup();
    } else if (opts && opts.keepOpen && addable.length === 0) {
      closePopup();
    }
  }

  /* ---------------- Add gift to cart ---------------- */
  function patchFromSection(html) {
    var doc = new DOMParser().parseFromString(html, "text/html");

    var newForm = doc.querySelector("#CartDrawer-Form");
    var curForm = document.querySelector("#CartDrawer-Form");
    if (newForm && curForm) curForm.innerHTML = newForm.innerHTML;

    var newProgress = doc.querySelector("#cart-progress");
    var curProgress = document.querySelector("#cart-progress");
    if (newProgress && curProgress) curProgress.innerHTML = newProgress.innerHTML;
  }

  function addGift(variantId, campaignHandle) {
    if (!variantId) return;
    window.theme && window.theme.cartDrawerBusy && window.theme.cartDrawerBusy.begin();

    // Block other add buttons on the popup until this request settles.
    addingGift = true;
    var busyPopup = getPopup();
    if (busyPopup) busyPopup.classList.add("is-busy");

    var wasOpen = !getPopup() || getPopup().hidden === false;
    var vid = Number(variantId);
    var handle = campaignHandle || "";
    var sections = ["cart-drawer", "cart-icon-bubble"];
    var lastCart = null;

    var applyResult = function (data) {
      lastCart = data;
      if (data && data.sections && data.sections["cart-drawer"]) {
        patchFromSection(data.sections["cart-drawer"]);
      }
      var bubble = data && data.sections && data.sections["cart-icon-bubble"];
      if (bubble) {
        var bubbleDoc = new DOMParser().parseFromString(bubble, "text/html");
        var newBubble = bubbleDoc.querySelector(".shopify-section");
        var curBubble = document.querySelector("#cart-icon-bubble");
        if (newBubble && curBubble) curBubble.innerHTML = newBubble.innerHTML;
      }
      if (typeof publish === "function" && typeof PUB_SUB_EVENTS !== "undefined") {
        publish(PUB_SUB_EVENTS.cartUpdate, { source: "gift-campaign", cartData: data });
      }
      initAll();
      syncPopupState({ keepOpen: wasOpen });
      flashGiftLine(variantId);
    };

    var addNewLine = function () {
      return fetch(window.Shopify.routes.root + "cart/add.js", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          items: [{ id: vid, quantity: 1, properties: { _free_gift: "true", _gift_campaign: handle } }],
          sections: sections,
        }),
      }).then(function (r) {
        return r.json();
      });
    };

    var incrementLine = function (lineKey, currentQty) {
      return fetch(window.Shopify.routes.root + "cart/change.js", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ id: lineKey, quantity: currentQty + 1, sections: sections }),
      }).then(function (r) {
        return r.json();
      });
    };

    // Consolidate: if a gift line for this variant + campaign already exists, bump
    // its quantity rather than creating a second line item.
    fetch(window.Shopify.routes.root + "cart.js", { headers: { "Content-Type": "application/json" } })
      .then(function (r) {
        return r.json();
      })
      .then(function (cart) {
        var existing = (cart.items || []).filter(function (it) {
          var p = it.properties || {};
          return it.variant_id === vid && p._gift_campaign === handle && p._free_gift === "true";
        })[0];
        return existing ? incrementLine(existing.key, existing.quantity) : addNewLine();
      })
      .then(applyResult)
      .catch(function (e) {
        console.error("[gift-campaign]", e);
      })
      .finally(function () {
        // Release the add lock and clear any stuck button/popup state (only lingers
        // if the add failed; on success the popup was re-rendered).
        addingGift = false;
        document.querySelectorAll(".gift-popup__add.is-adding").forEach(function (b) {
          b.classList.remove("is-adding");
        });
        document.querySelectorAll(".gift-popup.is-busy").forEach(function (p) {
          p.classList.remove("is-busy");
        });
        window.theme && window.theme.cartDrawerBusy && window.theme.cartDrawerBusy.end(lastCart);
      });
  }

  // Highlight the gift line that just landed in the cart drawer.
  function flashGiftLine(variantId) {
    var row = document.querySelector('.cart-item.free-gift[data-variant-id="' + variantId + '"]');
    if (!row) return;
    row.classList.remove("gift-added-flash");
    // Force reflow so the animation restarts even if the class lingered.
    void row.offsetWidth;
    row.classList.add("gift-added-flash");
    setTimeout(function () {
      row.classList.remove("gift-added-flash");
    }, 1300);
  }

  /* ---------------- Anti-abuse: remove/cap gifts no longer earned ---------------- */
  // Eligible campaigns are exactly those still rendered as popup sections (server-side
  // condition: collection spend >= threshold). Each carries its max allowance.
  function buildEligibleMap() {
    var map = {};
    document.querySelectorAll("[data-gift-section]").forEach(function (sec) {
      var h = sec.getAttribute("data-campaign-handle");
      if (!h) return;
      var max = parseInt(sec.getAttribute("data-max"), 10);
      map[h] = isNaN(max) ? Infinity : max;
    });
    return map;
  }

  // Returns { lineItemKey: targetQuantity } for gift lines that must shrink/be removed.
  function computeGiftUpdates(cart) {
    var eligible = buildEligibleMap();
    var counts = {};
    var updates = {};
    (cart.items || []).forEach(function (it) {
      var props = it.properties || {};
      var handle = props._gift_campaign;
      if (!handle) return; // only manage gifts added by this system
      var max = Object.prototype.hasOwnProperty.call(eligible, handle) ? eligible[handle] : 0;
      var already = counts[handle] || 0;
      var allowedRemaining = Math.max(0, max - already);
      var keep = Math.min(it.quantity, allowedRemaining);
      counts[handle] = already + keep;
      if (keep !== it.quantity) {
        updates[it.key] = keep;
      }
    });
    return updates;
  }

  function enforceGifts(cartData) {
    if (enforcing) return;

    var run = function (cart) {
      // The cart drawer patches #cart-progress before publishing cartUpdate, but
      // the previously relocated popup still lingers in <body> with stale sections.
      // Relocate first so eligibility is read from the freshly rendered popup only.
      relocatePopup();
      var updates = computeGiftUpdates(cart);
      var keys = Object.keys(updates);
      if (keys.length === 0) return;

      enforcing = true;

      // Animate the lines that are being fully removed, then commit after the
      // slide-out so the change reads clearly to the shopper.
      var animDelay = 0;
      keys.forEach(function (key) {
        if (updates[key] !== 0) return;
        var row = document.querySelector('.cart-item.free-gift[data-line-key="' + key + '"]');
        if (row) {
          row.classList.add("gift-removing");
          animDelay = 320;
        }
      });

      setTimeout(function () {
        commitUpdates(updates);
      }, animDelay);
    };

    var commitUpdates = function (updates) {
      window.theme && window.theme.cartDrawerBusy && window.theme.cartDrawerBusy.begin();
      var lastCart = null;
      fetch(window.Shopify.routes.root + "cart/update.js", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ updates: updates, sections: ["cart-drawer", "cart-icon-bubble"] }),
      })
        .then(function (r) {
          return r.json();
        })
        .then(function (data) {
          lastCart = data;
          if (data.sections && data.sections["cart-drawer"]) {
            patchFromSection(data.sections["cart-drawer"]);
          }
          var bubble = data.sections && data.sections["cart-icon-bubble"];
          if (bubble) {
            var bubbleDoc = new DOMParser().parseFromString(bubble, "text/html");
            var nb = bubbleDoc.querySelector(".shopify-section");
            var cb = document.querySelector("#cart-icon-bubble");
            if (nb && cb) cb.innerHTML = nb.innerHTML;
          }
          if (typeof publish === "function" && typeof PUB_SUB_EVENTS !== "undefined") {
            publish(PUB_SUB_EVENTS.cartUpdate, { source: "gift-campaign", cartData: data });
          }
          initAll();
          syncPopupState();
        })
        .catch(function (e) {
          console.error("[gift-campaign] enforce", e);
        })
        .finally(function () {
          enforcing = false;
          window.theme && window.theme.cartDrawerBusy && window.theme.cartDrawerBusy.end(lastCart);
        });
    };

    if (cartData && cartData.items) {
      run(cartData);
    } else {
      fetch(window.Shopify.routes.root + "cart.js", { headers: { "Content-Type": "application/json" } })
        .then(function (r) {
          return r.json();
        })
        .then(run)
        .catch(function (e) {
          console.error("[gift-campaign] enforce fetch", e);
        });
    }
  }

  /* ---------------- Delegated events (survive DOM re-renders) ---------------- */
  function bindDelegation() {
    if (document.__giftCampaignBound) return;
    document.__giftCampaignBound = true;

    document.addEventListener("click", function (e) {
      var openBtn = e.target.closest("[data-gift-open-popup]");
      if (openBtn) {
        e.preventDefault();
        // Only open if at least one campaign still has room to add a gift; all
        // limit-reached sections are hidden, so opening would show an empty popup.
        var canAddAny = getSectionStats().some(function (s) {
          return s.canAdd;
        });
        if (canAddAny) openPopup();
        return;
      }
      if (e.target.closest("[data-gift-popup-close]")) {
        e.preventDefault();
        closePopup();
        return;
      }
      // Click on the backdrop (anywhere inside the popup but outside the dialog).
      var popupEl = e.target.closest("[data-gift-popup]");
      if (popupEl && popupEl.hidden === false && !e.target.closest(".gift-popup__dialog")) {
        e.preventDefault();
        closePopup();
        return;
      }
      var addBtn = e.target.closest("[data-gift-add]");
      if (addBtn && !addBtn.disabled) {
        e.preventDefault();
        // Ignore clicks while another gift add is still in flight.
        if (addingGift) return;
        addBtn.classList.add("is-adding");
        var card = addBtn.closest(".gift-popup__product");
        if (card) card.classList.add("is-added");
        addGift(addBtn.getAttribute("data-variant-id"), addBtn.getAttribute("data-campaign-handle"));
        return;
      }
    });

    document.addEventListener("change", function (e) {
      var cb = e.target.closest("[data-gift-dismiss]");
      if (!cb) return;
      var handle = cb.getAttribute("data-campaign-handle");
      if (cb.checked) {
        addDismissed(handle);
      } else {
        removeDismissed(handle);
      }
    });

    document.addEventListener("keyup", function (e) {
      if (e.key === "Escape") closePopup();
    });
  }

  function initAll(root) {
    initCarousels(root);
  }

  function getDrawerEl() {
    return document.querySelector("cart-drawer");
  }

  function isDrawerOpen() {
    var d = getDrawerEl();
    return !!(d && d.classList.contains("active"));
  }

  // Runs after any drawer DOM mutation. Wires the (possibly freshly rendered)
  // carousel and relocates the popup. Idempotent: initCarousels/relocatePopup
  // no-op on already-processed nodes, so re-entry from our own mutations settles.
  // Auto-open only fires for a genuine cart change AND while the drawer is open
  // (e.g. quantity change inside the open drawer); never on page load.
  function processDrawerChange() {
    initAll();
    var genuineChange = autoOpenRequested;
    syncPopupState(genuineChange && isDrawerOpen() ? { openIfEligible: true } : {});
    autoOpenRequested = false;

    // Enforce AFTER the drawer has re-rendered so eligibility is read from the fresh
    // DOM (active-tier sections only). This is what removes lower-tier gift lines once
    // the shopper qualifies for a higher tier, regardless of which path triggered the
    // change (cart-items patch, product-form add-before-render, quick add, etc.).
    if (genuineChange) {
      var data = pendingCartData;
      pendingCartData = null;
      enforceGifts(data);
    }
  }

  // product-form publishes cartUpdate BEFORE renderContents, so reacting to the
  // event alone leaves the new gift bar uninitialised. Observing the drawer lets
  // us re-init after every render path (renderContents, section patch, quick-add).
  function setupObserver() {
    var target = document.getElementById("CartDrawer");
    if (!target || target.__giftObserver) return;
    var obs = new MutationObserver(function () {
      if (observerScheduled) return;
      observerScheduled = true;
      setTimeout(function () {
        observerScheduled = false;
        processDrawerChange();
      }, 40);
    });
    obs.observe(target, { childList: true, subtree: true });
    target.__giftObserver = obs;
  }

  // Watch the <cart-drawer> element's class so the popup auto-opens the moment the
  // drawer becomes visible (e.g. add-to-cart from PDP opens the drawer in a
  // setTimeout, after the content render). Page load keeps the drawer closed, so
  // no auto-open happens then.
  function setupDrawerOpenObserver() {
    var d = getDrawerEl();
    if (!d || d.__giftOpenObserver) return;
    drawerWasOpen = d.classList.contains("active");
    var obs = new MutationObserver(function () {
      var open = d.classList.contains("active");
      if (open && !drawerWasOpen) {
        initAll();
        syncPopupState({ openIfEligible: true });
      }
      drawerWasOpen = open;
    });
    obs.observe(d, { attributes: true, attributeFilter: ["class"] });
    d.__giftOpenObserver = obs;
  }

  function boot() {
    bindDelegation();
    initAll();
    // Relocate popup + sync dismiss checkboxes only. Do NOT auto-open on page load;
    // auto-open is driven by the drawer becoming visible / cart changes while open.
    syncPopupState();
    enforceGifts();
    setupObserver();
    setupDrawerOpenObserver();

    if (typeof subscribe === "function" && typeof PUB_SUB_EVENTS !== "undefined") {
      subscribe(PUB_SUB_EVENTS.cartUpdate, function (event) {
        if (event && event.source === "gift-campaign") return; // handled inline
        // A genuine cart change re-opens the popup if eligible and re-runs gift
        // enforcement. Both happen in processDrawerChange once the drawer has actually
        // re-rendered (the event may fire BEFORE render, so the DOM/eligibility is
        // stale here). Cache the payload to avoid a redundant cart.js fetch.
        autoOpenRequested = true;
        pendingCartData = event && event.cartData;
      });
    }
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", boot);
  } else {
    boot();
  }
})();
